import { useEffect, useState } from 'react';
// @ts-expect-error - Vite ?inline import
import lightThemeCss from '@milkdown/crepe/theme/nord.css?inline';
// @ts-expect-error - Vite ?inline import
import darkThemeCss from '@milkdown/crepe/theme/nord-dark.css?inline';

// @ts-expect-error - Vite ?inline import
import themeOverrides from './theme/overrides.css?inline';
import type { EditorTheme } from './types';

const THEME_STYLE_ID = 'milkdown-crepe-theme';

const getSystemTheme = (): 'light' | 'dark' => (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

/** Reads theme from <html> class (dark/light), falls back to system preference */
const getThemeFromHtml = (): 'light' | 'dark' => {
  const html = document.documentElement;
  if (html.classList.contains('dark')) return 'dark';
  if (html.classList.contains('light')) return 'light';

  return getSystemTheme();
};

export const useEditorTheme = (theme: EditorTheme = 'system') => {
  // Initialize with safe default for SSR - useEffect will set correct theme after hydration
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(theme === 'system' ? 'light' : theme);

  // Handle theme changes
  useEffect(() => {
    if (theme !== 'system') {
      setResolvedTheme(theme);

      return;
    }

    // Set initial theme from HTML class
    setResolvedTheme(getThemeFromHtml());

    // Watch for class changes on <html>
    const observer = new MutationObserver(() => {
      setResolvedTheme(getThemeFromHtml());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Also listen for system preference changes (fallback when no class set)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setResolvedTheme(getThemeFromHtml());
    mediaQuery.addEventListener('change', handler);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handler);
    };
  }, [theme]);

  // Inject theme CSS
  useEffect(() => {
    let style = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null;

    if (!style) {
      style = document.createElement('style');
      style.id = THEME_STYLE_ID;
      document.head.appendChild(style);
    }

    const themeCss = resolvedTheme === 'dark' ? darkThemeCss : lightThemeCss;
    style.textContent = themeCss + themeOverrides;
  }, [resolvedTheme]);

  return resolvedTheme;
};
